import React from 'react';

export enum MenuVariant {
  /**
   * Light background variant
   */
  Light = 'Light',
  /**
   * Dark/filled background variant
   */
  Filled = 'Filled',
}

export enum MenuSize {
  Small = 'Small',
  Medium = 'Medium',
}

export type MenuItemState =
  | 'enabled'
  | 'hovered'
  | 'pressed'
  | 'selected'
  | 'disabled';

export interface MenuItemProps {
  /**
   * Unique identifier for the menu item
   */
  id: string;
  /**
   * Label text for the menu item
   */
  label: string;
  /**
   * Leading icon element
   */
  leadingIcon?: React.ReactNode;
  /**
   * Whether to show trailing arrow (indicates submenu)
   */
  hasSubmenu?: boolean;
  /**
   * Trailing icon element (overrides submenu arrow)
   */
  trailingIcon?: React.ReactNode;
  /**
   * Whether the item is selected/active
   */
  selected?: boolean;
  /**
   * Whether the item is disabled
   */
  disabled?: boolean;
  /**
   * Tooltip text shown when the item is disabled (e.g. reason why editing is not allowed).
   */
  disabledTooltip?: string;
  /**
   * Side on which the disabled tooltip appears. Defaults to 'bottom'.
   */
  disabledTooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Click handler
   */
  onClick?: (id: string) => void;
  /**
   * Submenu items (renders nested menu on hover/click)
   */
  submenu?: MenuItemProps[];
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to show a divider after this item
   */
  divider?: boolean;
  /**
   * Custom text color for this item
   */
  textColor?: string;
  /**
   * Custom font weight for the label text
   */
  fontWeight?: number | string;
  /**
   * Custom icon color for this item
   */
  iconColor?: string;
  /**
   * Whether to show a checkbox for this item
   */
  showCheckbox?: boolean | undefined;
  /**
   * When true, show tooltip for long text. Otherwise, wrap text.
   */
  toolTip?: boolean | undefined;
}

export interface MenuProps {
  /**
   * Array of menu items
   */
  items: MenuItemProps[];
  /**
   * Menu variant
   */
  variant?: MenuVariant;
  /**
   * Menu size
   */
  size?: MenuSize;
  /**
   * Width of the menu
   */
  width?: number | string;
  /**
   * Whether to show leading icons
   */
  showLeadingIcons?: boolean;
  /**
   * Whether to show trailing arrows for items with submenus
   */
  showTrailingArrows?: boolean;
  /**
   * Whether submenus should expand on click (true) or hover (false)
   * @default true
   */
  expandOnClick?: boolean;
  /**
   * Callback when a menu item is clicked
   */
  onItemClick?: (id: string) => void;
  /**
   * Currently selected item ID or array of IDs (for multi-select)
   */
  selectedId?: string | string[];
  /**
   * Whether multiple items can be selected
   */
  multiSelect?: boolean;
  /**
   * Whether to show a search field at the top of the menu
   */
  showSearch?: boolean;
  /**
   * Placeholder for the search field
   */
  searchPlaceholder?: string | undefined;
  /**
   * Whether to show checkboxes for all items
   */
  showCheckboxes?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * When true, show tooltip for long text. Otherwise, wrap text.
   */
  toolTip?: boolean | undefined;
  /**
   * Callback when the user scrolls to the bottom of the menu
   */
  onScrollEnd?: (() => void) | undefined;
  /**
   * Callback fired whenever the search query changes (server-side search support)
   */
  onSearchChange?: (query: string) => void;
  /**
   * Maximum number of items visible before the list scrolls.
   * When set, the items container gets a fixed max-height and overflow-y: auto.
   */
  maxVisibleItems?: number;
  /**
   * Maximum height in pixels for the items list container (takes priority over maxVisibleItems).
   * When set, the items container gets this max-height and overflow-y: auto.
   */
  maxHeight?: number;
}

export interface MenuSizeConfig {
  itemHeight: number;
  paddingX: number;
  paddingY: number;
  fontSize: number;
  lineHeight: number;
  iconSize: number;
  gap: number;
  borderRadius: number;
}
